// One EXIT COLUMN (brief §3.2's two exits, §5.2): the terminal state's count + one-line summary from
// the projection, plus the reveal-recent affordance over the time-boxed `recentlyExited` window —
// rendered as quiet ghost cards that still open the item's card page. Goal wears the Forest tick,
// dead the quiet ⊘ (glyphs, not emoji). Machine state rides `data-exit`; the reveal toggle carries
// aria-expanded so tests assert behaviour, never copy (G-180).

import { useState } from 'react';
import type { BoardExitColumn } from '../../projections.ts';
import { stateLabel } from '../board-model.ts';
import { BoardItemCard } from './BoardItemCard.tsx';

export interface ExitColumnProps {
  readonly exit: BoardExitColumn;
  readonly onOpenCard: (itemId: string) => void;
}

/** One terminal column (goal or dead) at the end of the board. */
export function ExitColumn({ exit, onOpenCard }: ExitColumnProps): React.JSX.Element {
  const [showRecent, setShowRecent] = useState(false);
  const goal = exit.kind === 'goal';
  return (
    <div
      role="group"
      aria-label={`${stateLabel(exit.state)} — ${exit.kind} exit, ${String(exit.count)}`}
      data-exit={exit.kind}
      className="w-56 shrink-0 rounded-md border border-border bg-surface"
    >
      <div className="flex items-baseline justify-between gap-2 border-b border-border px-4 py-3">
        <span
          className={`ritona-numeric text-xs uppercase tracking-eyebrow ${goal ? 'text-forest' : 'text-text-subtle'}`}
        >
          {goal ? '✓' : '⊘'} {stateLabel(exit.state)}
        </span>
        <span className="ritona-numeric text-xs text-text-subtle">{exit.kind}</span>
      </div>
      <div className="p-3">
        <div className="ritona-numeric text-2xl text-text">{exit.count}</div>
        <p className="mt-1 text-xs leading-relaxed text-text-subtle">{exit.summary}</p>
        {exit.recentlyExited.length > 0 ? (
          <>
            <button
              type="button"
              aria-expanded={showRecent}
              onClick={() => setShowRecent((open) => !open)}
              className="mt-3 w-full rounded-sm border border-dashed border-border px-2 py-1.5 text-xs font-medium text-text-muted hover:border-border-strong hover:text-text"
            >
              {showRecent ? 'Hide recent' : 'Show recent'}
            </button>
            {showRecent ? (
              <div className="mt-2 flex flex-col gap-2">
                {exit.recentlyExited.map((item) => (
                  <BoardItemCard key={item.id} item={item} onOpen={onOpenCard} ghost />
                ))}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
